// Fleet Reports — Summary page (2026-07-14; all six metrics live 2026-07-16).
// The six KPI cards from the xlsx Summary sheet. A tile still renders its
// "coming soon" state (never a fake 0) for any metric the backend reports in
// meta.pendingMetrics — so if a future metric ships backend-first, the card
// degrades gracefully instead of showing zero.
import React from "react";
import { Card, Grid } from "@mui/material";
import StatTile from "./StatTile";

// Inclusive day count between two opaque YYYY-MM-DD keys (UTC arithmetic, no local
// Date parsing) — used only to annotate the selected period.
const daysBetween = (a, b) =>
  a && b ? Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86400000) + 1 : null;

export default function Summary({ report, isFetching }) {
  const m = (report && report.metrics) || {};
  const vehicles = (report && report.vehicles) || [];
  const nameOf = (sensorId) => {
    const v = vehicles.find((x) => x.sensorId === sensorId);
    return (v && v.name) || `sensor ${sensorId}`;
  };
  const pending = new Set((report && report.meta && report.meta.pendingMetrics) || []);
  const dq = (report && report.meta && report.meta.dataQuality) || [];

  // Idling is stored in MINUTES; the Summary headline shows HOURS (÷ its divisor).
  const idlingDiv = (m.idling && m.idling.weeklyDisplayDivisor) || 60;
  const idlingHours =
    m.idling && m.idling.fleetWeek !== null && m.idling.fleetWeek !== undefined
      ? m.idling.fleetWeek / idlingDiv
      : null;

  // Tile order mirrors the sidebar (owner decision 2026-07-23): fuel trio,
  // driver pair, idling, unplugged last.
  const tiles = [
    { key: "efficiency", label: "Fuel Efficiency", unit: "L/100km", better: "lower", value: m.efficiency && m.efficiency.fleetWeek, decimals: 2 },
    { key: "consumption", label: "Fuel Consumption", unit: "L", better: "lower", value: m.consumption && m.consumption.fleetWeek, decimals: 2 },
    // Fuel Cost (P3) only when the tenant has a price configured (metric present).
    ...(m.fuelCost
      ? [{ key: "fuelCost", label: "Fuel Cost", unit: m.fuelCost.currency || "USD", better: "lower", value: m.fuelCost.fleetWeek, decimals: 2 }]
      : []),
    { key: "drivingScore", label: "Driving Score", unit: "/ 10", better: "higher", value: m.drivingScore && m.drivingScore.fleetWeek, decimals: 2 },
    { key: "behavior", label: "Driving Behavior", unit: "events", better: "lower", value: m.behavior && m.behavior.fleetWeek, decimals: 0 },
    { key: "idling", label: "Idling Time", unit: "h", better: "lower", value: idlingHours, decimals: 2 },
    { key: "unplugged", label: "Unplugged Alerts", unit: "events", better: "lower", value: m.unplugged && m.unplugged.fleetWeek, decimals: 0 },
  ];

  const win = report && report.window;
  return (
    <div>
      {win && win.startDate ? (
        <p style={{ fontSize: "13px", color: "grey", margin: "0 0 16px 0" }}>
          Selected period: <b>{win.startDate} → {win.endDate}</b> ({daysBetween(win.startDate, win.endDate)} days). All headline figures are totals across this period.
        </p>
      ) : null}
      <Grid container spacing={2} style={{ marginBottom: "20px" }}>
        {tiles.map((t) => (
          <Grid item xs={12} sm={6} md={4} key={t.key}>
            <StatTile
              label={t.label}
              value={t.value === undefined ? null : t.value}
              unit={t.unit}
              better={t.better}
              decimals={t.decimals}
              loading={isFetching}
              pending={pending.has(t.key)}
            />
          </Grid>
        ))}
      </Grid>

      {dq.length > 0 ? (
        <Card style={{ padding: "16px" }}>
          <p style={{ fontSize: "15px", fontWeight: "bold", color: "#333333", margin: "0 0 10px 0", textTransform: "uppercase" }}>
            Data quality
          </p>
          {dq.map((d, i) => (
            <p key={i} style={{ fontSize: "13px", color: "grey", margin: "4px 0" }}>
              <b>{nameOf(d.sensorId)}</b>{d.date ? ` (${d.date})` : ""} - {d.flag === "no-data" ? "no telemetry in this window" : d.detail}
            </p>
          ))}
        </Card>
      ) : null}
    </div>
  );
}
